import axios from "axios";
import { createContext, useCallback, useContext, useEffect, useState } from "react";
import {
  getStoredMemberships,
  getStoredPermissions,
  persistAuthSession,
} from "../utils/permissions";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [me, setMe] = useState(null);
  // Hydratation synchrone depuis localStorage : évite un flash sans permissions
  const [permissions, setPermissions] = useState(getStoredPermissions);
  const [memberships, setMemberships] = useState(getStoredMemberships);
  const [loading, setLoading] = useState(false);

  const isAuthenticated = Boolean(localStorage.getItem("token"));

  const fetchMe = useCallback(async () => {
    if (!localStorage.getItem("token")) return null;
    try {
      setLoading(true);
      const { data } = await axios.get("/auth/me", { noLoading: true });
      setMe(data);
      const session = persistAuthSession(data);
      setPermissions(session.permissions);
      setMemberships(session.memberships);
      return data;
    } catch (error) {
      // Un 401 est géré par l'intercepteur (refresh/logout) ; on n'écrase pas
      // la session persistée sur une erreur réseau transitoire.
      console.error("Error fetching /auth/me:", error);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) fetchMe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hasPermission = useCallback(
    (perm) => permissions.includes(perm),
    [permissions]
  );

  const hasAnyPermission = useCallback(
    (...perms) => perms.some((p) => permissions.includes(p)),
    [permissions]
  );

  // Règle backend « 1 membre = 1 partenaire » : l'unique membership fait foi.
  const activeMembership = memberships[0] || null;

  const value = {
    me,
    user: me?.user || null,
    platform: me?.platform || null,
    permissions,
    memberships,
    activeMembership,
    isPlatformAdmin: Boolean(me?.platform),
    loading,
    fetchMe,
    hasPermission,
    hasAnyPermission,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuthContext = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuthContext must be used within an AuthProvider");
  }
  return context;
};
